import { preloadPostOfficeConfluenceSideNav } from '@confluence/experiment-post-office-side-nav/entry-points/preloadConfluenceSideNav.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_SPACE_PAGE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import {
	SYSTEM_HEADER_LEFT,
	SYSTEM_USER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadPersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/preloadPersistentInviteEligible.ts';
import { expValEquals } from '@confluence/feature-experiments';
import {
	preloadSuggestionCount,
	preloadSpaceCurationAuth,
} from '@confluence/space-curation/entry-points/preloadSpaceCuration.ts';
import { fg } from '@confluence/feature-gating';

import { preloadPlatformNavigationQuery } from './preloadPlatformNavigationQuery.ts';
import SpaceNavigationQuery, {
	type SpaceNavigationQuery$data,
	type SpaceNavigationQuery$variables,
} from './SpaceNavigation/__generated__/SpaceNavigationQuery.graphql.ts';

export async function preloadSideNavigation(spaceKey: string, previousSpaceKey?: string) {
	const preloadedSessionContext = getPreloaderFnContext();
	const { cloudId } = preloadedSessionContext;

	// Only preload space-curation data on SSR or when navigating to a different space.
	// Within-space navigation (page-to-page) can reuse the Apollo cache.
	const shouldPreloadSpaceCuration =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR) || previousSpaceKey !== spaceKey;

	const tasks: Array<Promise<any>> = [];

	if (fg('navx-global-side-nav-confluence')) {
		tasks.push(preloadPlatformNavigationQuery());
	}

	tasks.push(
		query<SpaceNavigationQuery$data, SpaceNavigationQuery$variables>({
			query: SpaceNavigationQuery,
			variables: {
				spaceKey,
				isLicensed: preloadedSessionContext.isLicensed,
			},
		}),
	);

	tasks.push(
		preloadUseExtensionList({
			spaceKey,
			moduleType: FORGE_MODULE_SPACE_PAGE,
		}),
	);

	tasks.push(preloadPersistentInviteButtonEligible({ spaceKey }, false));

	if (expValEquals('web_item_query_batch_system_call', 'isEnabled', true)) {
		// Keep distinct cache keys per location and let the transport batch the requests together.
		tasks.push(
			preloadWebItemLocation({
				location: SYSTEM_HEADER_LEFT,
				context: { single: false },
			}),
			preloadWebItemLocation({
				location: SYSTEM_USER,
				context: { single: false },
			}),
		);
	} else {
		// Fallback: Original behavior - only preload SYSTEM_HEADER_LEFT separately
		tasks.push(
			preloadWebItemLocation({
				location: SYSTEM_HEADER_LEFT,
			}),
		);
	}

	tasks.push(preloadPostOfficeConfluenceSideNav());

	if (
		shouldPreloadSpaceCuration &&
		expValEquals('confluence_autonomous_space_curation_ssr_streaming', 'isEnabled', true)
	) {
		tasks.push(
			slowQueryGuard(
				'preloadSpaceCuration',
				() =>
					createDataFetcherForSuspense('space-curation', () => [
						preloadSuggestionCount(spaceKey, cloudId),
						preloadSpaceCurationAuth(cloudId),
					]),
				SLOW_QUERY_DEFAULT_TIMEOUT,
			),
		);
	}

	return waitForAllReturnWhenError(tasks);
}
